import React from "react";
import { useNavigate } from "react-router-dom";
import { Num, ScoreBar, Badge } from "@/components/DataBits";
import { RSI_OVERBOUGHT, RSI_OVERSOLD, VOLUME_SURGE_THRESHOLD } from "@/lib/constants";

const COLS = [
  { key: "symbol", label: "SYMBOL", w: "w-24" },
  { key: "price", label: "LAST", w: "w-24", right: true },
  { key: "change_1d", label: "1D %", w: "w-20", right: true },
  { key: "change_5d", label: "5D %", w: "w-20", right: true },
  { key: "change_20d", label: "20D %", w: "w-20", right: true },
  { key: "rsi", label: "RSI", w: "w-16", right: true },
  { key: "macd_hist", label: "MACD H", w: "w-24", right: true },
  { key: "volume_surge", label: "VOL×", w: "w-16", right: true },
  { key: "momentum_score", label: "MOM SCORE", w: "w-40" },
];

function rsiClass(rsi) {
  if (rsi >= RSI_OVERBOUGHT) return "neg";
  if (rsi <= RSI_OVERSOLD) return "pos";
  return "";
}

function sortArrow(sortKey, sortDir, key) {
  if (sortKey !== key) return "";
  return sortDir === "asc" ? " ↑" : " ↓";
}

function watchBtnClass(active) {
  return active
    ? "border-[#00e676] text-[#00e676]"
    : "border-[#222] text-[#8a8a8e] hover:text-white";
}

function MomentumRow({ r, index, inWatch, onWatchToggle }) {
  const nav = useNavigate();
  return (
    <tr
      onClick={() => nav(`/stock/${r.symbol}`)}
      className="data-row border-b border-[#1a1a1a] cursor-pointer"
      data-testid={`momentum-row-${r.symbol}`}
    >
      <td className="px-3 py-2 text-[#555] font-mono-data text-xs text-right">{index + 1}</td>
      <td className="px-3 py-2">
        <span className="font-mono-data font-semibold tracking-wider">{r.symbol}</span>
        {!r.optionable && <Badge>NO-OPT</Badge>}
      </td>
      <td className="px-3 py-2 text-right"><Num value={r.price} /></td>
      <td className="px-3 py-2 text-right"><Num value={r.change_1d} signed suffix="%" /></td>
      <td className="px-3 py-2 text-right"><Num value={r.change_5d} signed suffix="%" /></td>
      <td className="px-3 py-2 text-right"><Num value={r.change_20d} signed suffix="%" /></td>
      <td className="px-3 py-2 text-right">
        <span className={`font-mono-data ${rsiClass(r.rsi)}`}>{r.rsi.toFixed(1)}</span>
      </td>
      <td className="px-3 py-2 text-right">
        <span className={`font-mono-data ${r.macd_hist >= 0 ? "pos" : "neg"}`}>
          {r.macd_hist >= 0 ? "+" : ""}{r.macd_hist.toFixed(3)}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        <span className={`font-mono-data ${r.volume_surge >= VOLUME_SURGE_THRESHOLD ? "warn" : ""}`}>
          {r.volume_surge.toFixed(2)}×
        </span>
      </td>
      <td className="px-3 py-2"><ScoreBar score={r.momentum_score} /></td>
      <td className="px-3 py-2 text-center">
        <button
          onClick={(e) => onWatchToggle(e, r.symbol)}
          className={`text-[10px] font-mono-data px-2 py-0.5 border ${watchBtnClass(inWatch)}`}
          data-testid={`watch-toggle-${r.symbol}`}
        >
          {inWatch ? "ON" : "ADD"}
        </button>
      </td>
    </tr>
  );
}

export default function MomentumTable({
  rows, loading, sortKey, sortDir, onSort,
  isInWatch, onWatchToggle,
}) {
  return (
    <div className="panel">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="momentum-table">
          <thead>
            <tr className="border-b border-[#222] bg-[#0a0a0a]">
              <th className="w-8 px-3 py-2"></th>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  onClick={() => onSort(c.key)}
                  className={`px-3 py-2 micro-label cursor-pointer hover:text-white ${c.right ? "text-right" : "text-left"} ${c.w}`}
                  data-testid={`sort-${c.key}`}
                >
                  {c.label}{sortArrow(sortKey, sortDir, c.key)}
                </th>
              ))}
              <th className="px-3 py-2 micro-label text-center w-20">WATCH</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={COLS.length + 2} className="px-3 py-10 text-center text-[#555] font-mono-data text-xs">LOADING MARKET DATA…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={COLS.length + 2} className="px-3 py-10 text-center text-[#555] font-mono-data text-xs">NO DATA</td></tr>
            )}
            {rows.map((r, i) => (
              <MomentumRow
                key={r.symbol}
                r={r}
                index={i}
                inWatch={isInWatch(r.symbol)}
                onWatchToggle={onWatchToggle}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
